import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ArrowLeft } from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser";
import { useLeague } from "@/features/leagues/hooks/useLeague";
import { useManagerProfile } from "@/features/leagues/standings/hooks/useManagerProfile";
import { ManagerProfileHeader } from "@/features/leagues/managers/components/ManagerProfileHeader";
import { ManagerRosterTable } from "@/features/leagues/managers/components/ManagerRosterTable";
import { ManagerContributionsTable } from "@/features/leagues/managers/components/ManagerContributionsTable";
import { PageContainer } from "@/shared/layout/PageContainer";
import { Section } from "@/shared/layout/Section";
import { Skeleton } from "@/shared/ui/Skeleton";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute(
  "/_authenticated/leagues/$leagueId/managers/$memberId",
)({
  component: ManagerProfilePage,
});

function ManagerProfilePage() {
  const { leagueId, memberId } = Route.useParams();
  const t = useT();
  const me = useCurrentUser();
  const league = useLeague(leagueId as Id<"leagues">);
  const profile = useManagerProfile(
    leagueId as Id<"leagues">,
    memberId as Id<"leagueMembers">,
  );
  const contributions = useQuery(api.standings.listLeagueContributions, {
    leagueId: leagueId as Id<"leagues">,
    memberId: memberId as Id<"leagueMembers">,
    limit: 10,
  });
  const canOfferTrades =
    league?.league.state === "active" &&
    me !== undefined &&
    me !== null &&
    profile !== undefined &&
    profile.member.userId !== me._id;

  return (
    <PageContainer>
      <Link
        to="/leagues/$leagueId/standings"
        params={{ leagueId }}
        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-muted hover:text-ink transition-colors mb-4"
      >
        <ArrowLeft size={14} />
        {t("league.profile.backToStandings")}
      </Link>

      {profile === undefined || contributions === undefined ? (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-12 w-2/3" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          <ManagerProfileHeader profile={profile} />

          <Section
            title={t("league.profile.roster.title")}
            description={t("league.profile.roster.subtitle")}
          >
            <ManagerRosterTable
              rows={profile.roster}
              leagueId={leagueId as Id<"leagues">}
              canOfferTrades={canOfferTrades}
            />
          </Section>

          <Section
            title={t("league.profile.recent.title")}
            description={t("league.profile.recent.subtitle")}
          >
            <ManagerContributionsTable rows={contributions} leagueId={leagueId as Id<"leagues">} />
          </Section>
        </div>
      )}
    </PageContainer>
  );
}
